import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

import useSelectedTenant from '../../../../../components/hooks/useSelectedTenant';
import useCurrencyService from '../../../../../components/hooks/useCurrencyService';
import SummaryTiles, { TILE_ACCENT } from '../../../../../components/utils/SummaryTiles';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';

import LadderEditorModal from './LadderEditorModal';

import { GetInternationalServiceFeesBIZcore } from '../../../../../redux/actions/business/core/internationalServiceFeesActions';
import { GetInternationalUSDtoUSDServiceFeesBIZcore } from '../../../../../redux/actions/business/core/internationalUSDtoUSDServiceFeesActions';
import { GetServicesBIZsubs } from '../../../../../redux/actions/business/subscriptions/servicesActions';

const LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';

// max_trx_amount of -1 (or 0 on the top band) means "no upper bound".
const UNBOUNDED = -1;
const isUnbounded = (max) => Number(max) === UNBOUNDED;

const SOURCES = [
  { key: 'international', label: 'International' },
  { key: 'usd', label: 'USD → USD' },
];

/**
 * A "lane" is one ladder of amount bands: all the rows sharing the same
 * (table, service id, fee name). International fees are laddered per lane —
 * FX.Inbound and FX.Outbound are separate ladders under the same service id,
 * so grouping by service id alone would interleave two ranges and invent
 * overlaps that do not exist.
 */
const buildLanes = (rows, sourceKey, sourceLabel) => {
  const lanes = {};
  rows.forEach((row) => {
    const serviceId = String(row.ref_service_id ?? "").trim() || "(blank)";
    const feeName = row.fee_name || "(unnamed)";
    const id = `${sourceKey}::${serviceId}::${feeName}`;
    if (!lanes[id]) {
      lanes[id] = { id, sourceKey, sourceLabel, serviceId, feeName, bands: [] };
    }
    lanes[id].bands.push(row);
  });

  return Object.values(lanes).map((lane) => {
    const bands = [...lane.bands].sort(
      (a, b) => Number(a.min_trx_amount) - Number(b.min_trx_amount));

    // Bands are min-inclusive / max-exclusive, so touching boundaries
    // (…-50000 then 50000-…) are contiguous, not overlapping.
    const issues = [];
    for (let i = 1; i < bands.length; i++) {
      const prev = bands[i - 1];
      const cur = bands[i];
      if (isUnbounded(prev.max_trx_amount)) {
        issues.push({ type: 'unreachable', at: i,
          detail: 'a band sits above one that is already unbounded' });
        continue;
      }
      const prevMax = Number(prev.max_trx_amount);
      const curMin = Number(cur.min_trx_amount);
      if (curMin > prevMax) {
        issues.push({ type: 'gap', at: i, from: prevMax, to: curMin,
          detail: 'amounts in this window match no band' });
      } else if (curMin < prevMax) {
        issues.push({ type: 'overlap', at: i, from: curMin, to: prevMax,
          detail: 'two bands both claim this window' });
      }
    }
    const first = bands[0];
    if (first && Number(first.min_trx_amount) > 0) {
      issues.unshift({ type: 'gap', at: 0, from: 0, to: Number(first.min_trx_amount),
        detail: 'amounts below the first band match no band' });
    }
    const last = bands[bands.length - 1];
    const openEnded = last ? isUnbounded(last.max_trx_amount) : false;
    if (last && !openEnded) {
      issues.push({ type: 'open-top', at: bands.length,
        detail: 'nothing covers amounts above the highest band' });
    }
    return { ...lane, bands, issues, openEnded };
  }).sort((a, b) =>
    a.serviceId.localeCompare(b.serviceId, undefined, { numeric: true })
    || a.feeName.localeCompare(b.feeName));
};

// Label above value, with the label muted and the value strong. Previously both
// were the same size and weight, which made the row hard to read at a glance.
const Field = ({ label, value }) => (
  <Box>
    <Typography variant='caption'
      sx={{ color: LABEL_COLOR, display: "block", fontSize: "11px",
            letterSpacing: "0.4px", textTransform: "uppercase" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap" }}>
      {value}
    </Typography>
  </Box>
);

const ISSUE_COLOR = { gap: TILE_ACCENT.bad, overlap: TILE_ACCENT.warn, 'open-top': TILE_ACCENT.warn, unreachable: TILE_ACCENT.warn };

const FeeRangesPage = () => {
  const dispatch = useDispatch();
  const tenantId = useSelectedTenant();
  const { handleShowAmount } = useCurrencyService();

  const coreState = useSelector(state => state["business.core"]);
  const subsState = useSelector(state => state["business.subscriptions"]);

  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState("");
  const [search, setSearch] = useState("");
  const [issuesOnly, setIssuesOnly] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingLane, setEditingLane] = useState(null);

  useEffect(() => {
    handleGetAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function handleGetAll() {
    setLoading(true);
    // Whole set, not a page: a ladder split across pages would report
    // gaps that are only an artefact of pagination.
    await Promise.all([
      dispatch(GetInternationalServiceFeesBIZcore(tenantId, 1, 1000, "", () => {})),
      dispatch(GetInternationalUSDtoUSDServiceFeesBIZcore(tenantId, 1, 1000, "", () => {})),
      dispatch(GetServicesBIZsubs(1, 1000, "", () => {})),
    ]);
    setLoading(false);
  }

  const services = useMemo(() => subsState?.servicesList?.records || [], [subsState]);
  const serviceCode = (id) =>
    services.find((s) => String(s.id) === String(id))?.service_code || null;

  const lanes = useMemo(() => {
    const intl = coreState?.internationalServiceFeesList?.records || [];
    const usd = coreState?.internationalUSDtoUSDServiceFeesList?.records || [];
    return [
      ...buildLanes(intl, 'international', 'International'),
      ...buildLanes(usd, 'usd', 'USD → USD'),
    ];
  }, [coreState]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return lanes
      .filter((l) => !sourceFilter || l.sourceKey === sourceFilter)
      .filter((l) => !issuesOnly || l.issues.length > 0)
      .filter((l) => !term
        || l.serviceId.toLowerCase().includes(term)
        || l.feeName.toLowerCase().includes(term)
        || (serviceCode(l.serviceId) || "").toLowerCase().includes(term));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, sourceFilter, issuesOnly, search, services]);

  const totalBands = lanes.reduce((n, l) => n + l.bands.length, 0);
  const lanesWithIssues = lanes.filter((l) => l.issues.length > 0);

  const summaryTiles = [
    { label: "Ladders", value: lanes.length, accent: TILE_ACCENT.neutral },
    { label: "Bands", value: totalBands, accent: TILE_ACCENT.neutral },
    { label: "Clean ladders", value: lanes.length - lanesWithIssues.length, accent: TILE_ACCENT.good },
    { label: "With gaps / overlaps", value: lanesWithIssues.length, accent: lanesWithIssues.length ? TILE_ACCENT.bad : TILE_ACCENT.good },
  ];

  const amount = (currency, value) =>
    isUnbounded(value) ? "∞" : handleShowAmount(currency, value || 0);

  return (
    <Box>
      <LadderEditorModal
        open={editorOpen}
        setOpen={setEditorOpen}
        lane={editingLane}
        tenantId={tenantId}
        handleGet={handleGetAll}
        />

      <TopBar
        title={"Fee Ranges"}
        search={search}
        searchPlaceholder={"Service id, service code or lane name"}
        onSearchChange={(e) => setSearch(e.target.value)}
        button1Text={"Refresh"}
        button1OnClick={() => handleGetAll()}
        />

      <Typography
        variant='caption'
        sx={{ color: LABEL_COLOR, display: "block", mb: "16px" }}>
        Each ladder is one service id and one lane (e.g. FX.Inbound), with its
        amount bands in order. Bands are min-inclusive and max-exclusive, so
        touching edges are contiguous. Read-only.
      </Typography>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
        <Box>
          <Box className='flexCenter_Row' flexWrap={"wrap"} gap={"12px"} mb={"16px"}>
            <TextField
              select size='small' label='Table' sx={{ minWidth: "180px" }}
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}>
              <MenuItem value=''>Both tables</MenuItem>
              {SOURCES.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
            </TextField>
            <TextField
              select size='small' label='Show' sx={{ minWidth: "180px" }}
              value={issuesOnly}
              onChange={(e) => setIssuesOnly(e.target.value)}>
              <MenuItem value=''>All ladders</MenuItem>
              <MenuItem value='issues'>Only with gaps / overlaps</MenuItem>
            </TextField>
          </Box>

          <SummaryTiles tiles={summaryTiles} />

          {
            visible.length === 0
            ?
            <Box height={"200px"} className="flexCenterCenterRow">
              <Typography>No ladder matches this filter</Typography>
            </Box>
            :
            <Box display={"flex"} flexDirection={"column"} gap={"14px"}>
              {
                visible.map((lane) => (
                  <Box
                    key={lane.id}
                    className="block"
                    sx={{ padding: "16px" }}>

                    {/* ---------- ladder header ---------- */}
                    <Box className='flexCenterSBRow' flexWrap={"wrap"} gap={"10px"} mb={"12px"}>
                      <Box className='flexCenter_Row' gap={"10px"} flexWrap={"wrap"}>
                        <Typography variant='subtitle2'>{lane.feeName}</Typography>
                        <Chip size='small' variant='outlined' label={lane.sourceLabel} color="warning" />
                        <Typography variant='caption' color={"grey"}>
                          service id&nbsp;<Copyable>{lane.serviceId}</Copyable>
                        </Typography>
                        {
                          serviceCode(lane.serviceId)
                          &&
                          <Typography variant='caption' sx={{ color: LABEL_COLOR, fontFamily: "monospace" }}>
                            {serviceCode(lane.serviceId)}
                          </Typography>
                        }
                      </Box>
                      {
                        lane.issues.length === 0
                        ? <Chip size='small' icon={<CheckCircleOutlineOutlinedIcon />} label="contiguous" color='success' variant='outlined' />
                        : <Chip size='small' icon={<ReportProblemOutlinedIcon />} label={`${lane.issues.length} issue${lane.issues.length === 1 ? "" : "s"}`} color='error' variant='filled' />
                      }
                        <Button
                          size='small'
                          variant='outlined'
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => {
                            setEditingLane(lane);
                            setEditorOpen(true);
                          }}>
                          Edit ladder
                        </Button>
                    </Box>

                    {/* ---------- the bands, in order, with issues interleaved ---------- */}
                    <Box display={"flex"} flexDirection={"column"} gap={"4px"}>
                      {
                        lane.bands.map((band, i) => (
                          <React.Fragment key={i}>
                            {
                              lane.issues.filter((x) => x.at === i).map((issue, k) => (
                                <Box
                                  key={`i${k}`}
                                  className='flexCenter_Row'
                                  gap={"8px"}
                                  sx={{
                                    border: `1px dashed ${ISSUE_COLOR[issue.type]}80`,
                                    background: `${ISSUE_COLOR[issue.type]}14`,
                                    borderRadius: "8px",
                                    padding: "6px 12px",
                                  }}>
                                  <ReportProblemOutlinedIcon sx={{ fontSize: "16px", color: ISSUE_COLOR[issue.type] }} />
                                  <Typography variant='caption'>
                                    <b>{issue.type}</b>
                                    {
                                      issue.from !== undefined
                                      && ` ${amount(band.fee_currency, issue.from)} → ${amount(band.fee_currency, issue.to)}`
                                    }
                                    &nbsp;· {issue.detail}
                                  </Typography>
                                </Box>
                              ))
                            }
                            <Box
                              className='flexCenterSBRow'
                              flexWrap={"wrap"}
                              gap={"10px"}
                              sx={{
                                border: "1px solid rgba(44, 45, 47, 1)",
                                background: "rgba(44, 45, 47, 0.44)",
                                borderRadius: "8px",
                                padding: "8px 12px",
                                opacity: band.is_active === false ? 0.5 : 1,
                              }}>
                              <Box sx={{ minWidth: "260px" }}>
                                <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block" }}>
                                  Amount range
                                </Typography>
                                <Typography sx={{ fontFamily: "monospace", fontSize: "15px", fontWeight: 600 }}>
                                  {amount(band.fee_currency, band.min_trx_amount)}
                                  &nbsp;→&nbsp;
                                  {amount(band.fee_currency, band.max_trx_amount)}
                                </Typography>
                              </Box>
                              <Box className='flexCenter_Row' gap={"28px"} flexWrap={"wrap"}>
                                <Field label="FX markup" value={`${band.markup_percent}%`} />
                                <Field label="Regular fee"
                                  value={handleShowAmount(band.fee_currency, band.fee_amount_regular || 0)} />
                                <Field label="Priority fee"
                                  value={handleShowAmount(band.fee_currency, band.fee_amount_priority || 0)} />
                                {
                                  band.is_active === false
                                  && <Chip size='small' label="inactive" color='warning' variant='outlined' />
                                }
                              </Box>
                            </Box>
                          </React.Fragment>
                        ))
                      }
                      {
                        lane.issues.filter((x) => x.at === lane.bands.length).map((issue, k) => (
                          <Box
                            key={`t${k}`}
                            className='flexCenter_Row'
                            gap={"8px"}
                            sx={{
                              border: `1px dashed ${ISSUE_COLOR[issue.type]}80`,
                              background: `${ISSUE_COLOR[issue.type]}14`,
                              borderRadius: "8px",
                              padding: "6px 12px",
                            }}>
                            <ReportProblemOutlinedIcon sx={{ fontSize: "16px", color: ISSUE_COLOR[issue.type] }} />
                            <Typography variant='caption'>
                              <b>{issue.type}</b> · {issue.detail}
                            </Typography>
                          </Box>
                        ))
                      }
                    </Box>
                  </Box>
                ))
              }
            </Box>
          }
        </Box>
      }
    </Box>
  )
}

export default FeeRangesPage;
